/**
 * CompanyTable - Desktop table view for company list
 * List card scrolls horizontally when the viewport is narrower than the table min width.
 */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import type { CompanyTableProps } from '../types'
import { CompanyTableSortColumnHeader } from './CompanyTableSortHeader'
import { useUserRole } from '@/hooks/useUserRole'
import { formatPafSummaryDate } from '../utils/pafDateFormat'
import { cn } from '@/lib/utils/helpers'
import styles from '@/features/invitee/styles/invitesParity.module.css'
import '../styles/companyTable.css'

function buildCompanyColWeights(isLiberty: boolean): number[] {
  const w: number[] = [12, 4, 4, 6, 6, 8, 7, 6]
  if (isLiberty) w.push(5, 4)
  w.push(6)
  if (isLiberty) w.push(4)
  return w
}

function colWidthsFromWeights(weights: number[]) {
  const s = weights.reduce((a, b) => a + b, 0)
  return weights.map((n) => `${(n * 100) / s}%`)
}

export function CompanyTable({
  companies,
  onSort,
  sortBy,
  orderBy,
  onToggleListed,
  isCompanyActive,
  disabled = false,
  loading = false,
}: CompanyTableProps) {
  const { isLibertyRole, hasPolicy } = useUserRole()
  const canEditListed = hasPolicy(['company_listed_edit'])
  const listedDisabled = disabled || !canEditListed

  const colWidths = useMemo(
    () => colWidthsFromWeights(buildCompanyColWeights(isLibertyRole)),
    [isLibertyRole]
  )
  const colCount = colWidths.length

  return (
    <div data-testid="company-list-table-card" className={styles.tableCard}>
      <div
        data-testid="company-list-table-scroll"
        className={cn(styles.tableResponsiveScroll, 'touch-scroll')}
      >
        <table
          className={cn(
            styles.listingTable,
            styles.companyListingTable,
            'table-fixed w-full min-w-[85rem] !max-w-none [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal',
            '[&_tbody_td:not([colspan])]:break-words',
            /* Match header cell padding to body so columns line up */
            '[&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle',
            '[&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug',
          )}
          data-testid="company-list-table"
        >
          <colgroup>
            {colWidths.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead data-testid="company-list-table-head" className={disabled ? 'pointer-events-none' : undefined}>
            <tr>
              <CompanyTableSortColumnHeader field="company_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                Company Name
              </CompanyTableSortColumnHeader>
              <CompanyTableSortColumnHeader field="company_id" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                ID
              </CompanyTableSortColumnHeader>
              <CompanyTableSortColumnHeader field="company_state" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                State
              </CompanyTableSortColumnHeader>
              <CompanyTableSortColumnHeader field="company_city" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                City
              </CompanyTableSortColumnHeader>
              <CompanyTableSortColumnHeader field="contact_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                Contact Name
              </CompanyTableSortColumnHeader>
              <CompanyTableSortColumnHeader field="contact_email" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                Contact Email
              </CompanyTableSortColumnHeader>
              <CompanyTableSortColumnHeader field="salesperson" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                LR Relationship
              </CompanyTableSortColumnHeader>
              <CompanyTableSortColumnHeader align="center" field="units_number" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                Unit Count
              </CompanyTableSortColumnHeader>
              {isLibertyRole && (
                <CompanyTableSortColumnHeader field="company_alias_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                  Alias Name
                </CompanyTableSortColumnHeader>
              )}
              {isLibertyRole && (
                <CompanyTableSortColumnHeader align="center" field="itrratio" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                  ITR Value
                </CompanyTableSortColumnHeader>
              )}
              <th
                data-testid="company-list-column-liberty-listed-header"
                className="!text-center !align-middle"
              >
                Liberty Listed
              </th>
              {isLibertyRole && (
                <CompanyTableSortColumnHeader field="deleted_at" orderBy={orderBy} sortBy={sortBy} onSort={onSort} disabled={disabled}>
                  Deleted
                </CompanyTableSortColumnHeader>
              )}
            </tr>
          </thead>
          <tbody data-testid="company-list-table-body">
            {loading ? (
              <ListingTableLoadingRow
                colSpan={colCount}
                data-testid="company-list-table-loading-row"
              />
            ) : companies.length === 0 ? (
              <tr data-testid="company-list-table-empty-row">
                <td colSpan={colCount} className="!py-12 text-center text-slate-500">
                  No companies found
                </td>
              </tr>
            ) : (
              companies.map((company) => {
                const active = isCompanyActive ? isCompanyActive(company) : company.org_listed_ids?.['1'] === 1
                return (
                <tr
                  key={company.company_id}
                  data-testid={`company-list-row-${company.company_id}`}
                  className="transition-colors"
                >
                  <td>
                    <Link
                      data-testid={`company-list-row-${company.company_id}-name-link`}
                      to={`/company/edit/${company.company_id}`}
                      className="inline-block text-left text-[13px] font-medium text-[rgb(21_151_255)] transition-colors hover:text-[#0d7acc] hover:underline"
                    >
                      {company.company_name}
                    </Link>
                  </td>
                  <td className="text-slate-600">{company.company_id}</td>
                  <td className="text-slate-600">{company.company_state}</td>
                  <td className="text-slate-600">{company.company_city}</td>
                  <td className="text-slate-600">{company.contact_name}</td>
                  <td className="text-slate-600 break-all">{company.contact_email}</td>
                  <td className="text-slate-600">{company.salesperson}</td>
                  <td className="!text-center text-slate-600 tabular-nums">{company.units_number}</td>
                  {isLibertyRole && <td className="text-slate-600">{company.company_alias_name}</td>}
                  {isLibertyRole && (
                    <td className="!text-center text-slate-600 tabular-nums">{company.itrratio}</td>
                  )}
                  <td className="!text-center">
                    <button
                      data-testid={`company-list-row-${company.company_id}-liberty-listed-toggle`}
                      onClick={() => onToggleListed?.(company)}
                      disabled={listedDisabled}
                      className={cn(
                        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold transition-all',
                        active
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : 'bg-red-100 text-red-700 hover:bg-red-200',
                        listedDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                      )}
                      type="button"
                    >
                      {active ? 'Active' : 'Not Active'}
                    </button>
                  </td>
                  {isLibertyRole && (
                    <td className="text-slate-600 whitespace-nowrap">
                      {company.deleted_at
                        ? formatPafSummaryDate(company.deleted_at) || ''
                        : ''}
                    </td>
                  )}
                </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
